<template>
  <v-container fluid class="pa-0">
    <v-toolbar color="grey-lighten-3">
      <v-toolbar-title class="text-h6 font-weight-medium">
        OD Integration
      </v-toolbar-title>
    </v-toolbar>

    <v-tabs v-model="tab"  align-tabs="start" color="primary">
      <v-tab value="accessToken">OD Integration</v-tab>
      <v-tab value="connector">OD Connector</v-tab>
      <v-tab value="syncLogs">Sync Logs</v-tab>
    </v-tabs>

    <v-window v-model="tab">

      <v-window-item value="accessToken">
        <v-row class="mt-4 ml-4">
          <v-col>
            <p class="text-body-2">
              Please enter the OD access token for Open Dental integration. This token is crucial for
              establishing a secure connection with your Open Dental server.
            </p>
          </v-col>
        </v-row>

        <v-row class="mt-2 ml-4">
          <v-col cols="12" md="6">
            <v-label>OD Access Token</v-label>
            <v-text-field
              v-model="odAccessToken"
              variant="outlined"
              density="comfortable"
              :append-icon="showToken ? 'mdi-eye' : 'mdi-eye-off'"
              :type="showToken ? 'text' : 'password'"
              @click:append="showToken = !showToken"
              hide-details
              style="max-width: 600px;"
              autocomplete="new-password"
            >
          </v-text-field>
            <v-btn color="primary" class="mt-4" @click="handleAccessToken">
              {{isODEnabled ? 'Update' : 'Save'}} Access Token
            </v-btn>
          </v-col>
        </v-row>
         <v-divider class="mt-10"></v-divider>
          <v-toolbar density="compact" title="Clinics" color="success"></v-toolbar>
          <v-progress-linear :active="loading" color="blue-lighten-3" :indeterminate="loading"></v-progress-linear>
          <v-data-table-virtual
            :headers="clinicHeaders"
            :items="clinics"
            height="400"
            item-value="name"
            fixed-header
          ></v-data-table-virtual>
      </v-window-item>

      <v-window-item value="connector">
        <v-row class="mt-4 ml-4">
          <v-col>
            <p class="text-body-2">
              Please download
              <a :href="agentExeUrl" download>dss-od-connector.exe</a>
              to the computer where your Open Dental server is installed. After downloading,
              right-click the file and choose <strong>Run as administrator</strong>. When prompted,
              enter the integration token provided on this page by copying and pasting it into
              the application. This process is necessary to complete the integration setup.
            </p>
          </v-col>
        </v-row>

        <v-row class="mt-2 ml-4">
          <v-col cols="12" md="6">
            <v-label>Integration token</v-label>
            <v-text-field
              v-model="maskedIntegrationToken"
              readonly
              variant="outlined"
              density="comfortable"
              hide-details
              style="max-width: 400px; background-color: #eee;"
            >
              <template #append-inner>
                <v-fade-transition mode="out-in">
                  <v-icon
                    v-if="copySuccess"
                    key="check"
                    color="green"
                  >
                    mdi-check
                  </v-icon>
                  <v-icon
                    v-else
                    key="copy"
                    @click="copyToken"
                    class="cursor-pointer"
                  >
                    mdi-content-copy
                  </v-icon>
                </v-fade-transition>
              </template>
            </v-text-field>
          </v-col>
        </v-row>
      </v-window-item>

      <v-window-item value="syncLogs">
        <v-row class="mt-4 ml-4 mr-4 align-center">
          <v-col cols="12" md="3">
            <v-select
              v-model="syncLogFilter.action"
              :items="['ALL','CREATED','UPDATED']"
              label="Action"
              variant="outlined"
              density="comfortable"
              clearable
              hide-details
            />
          </v-col>
          <v-col cols="12" md="3">
            <v-text-field
              v-model="syncLogFilter.from"
              label="From Date"
              type="date"
              variant="outlined"
              density="comfortable"
              clearable
              hide-details
            />
          </v-col>
          <v-col cols="12" md="3">
            <v-text-field
              v-model="syncLogFilter.to"
              label="To Date"
              type="date"
              variant="outlined"
              density="comfortable"
              clearable
              hide-details
            />
          </v-col>
          <v-col cols="12" md="3">
            <v-btn color="primary" @click="fetchSyncLogs(true)">Search</v-btn>
            <v-btn class="ml-2" variant="text" @click="resetSyncLogFilters">Reset</v-btn>
          </v-col>
        </v-row>
        <v-row class="mt-1 ml-4">
          <v-col class="text-caption text-grey">
            Showing {{ syncLogs.length }} of {{ syncLogTotal }} entries
          </v-col>
        </v-row>
        <v-progress-linear :active="syncLogLoading" color="blue-lighten-3" indeterminate />
        <v-data-table-virtual
          :headers="syncLogHeaders"
          :items="syncLogs"
          item-value="id"
          height="480"
          fixed-header
        >
          <template v-slot:item.synced_at="{ item }">
            {{ simpleDateTimeFormat(item.synced_at) }}
          </template>
          <template v-slot:item.action="{ item }">
            <v-chip
              :color="item.action === 'CREATED' ? 'green' : item.action === 'UPDATED' ? 'blue' : 'grey'"
              size="small"
              label
            >{{ item.action }}</v-chip>
          </template>
          <template v-slot:item.contact_id="{ item }">
            <span v-if="item.contact_id" style="color:#1976D2;font-weight:500;">
              #{{ item.contact_id }}
            </span>
            <span v-else class="text-grey">—</span>
          </template>
        </v-data-table-virtual>
        <v-row class="mt-2 ml-4" v-if="syncLogs.length < syncLogTotal">
          <v-col>
            <v-btn variant="text" color="primary" @click="fetchSyncLogs(false)" :loading="syncLogLoading">
              Load more
            </v-btn>
          </v-col>
        </v-row>
      </v-window-item>

    </v-window>
  </v-container>
</template>

<script>
import ODConnectorService from '@/services/odConnectorService';
import ODService from '@/services/odService';
import EventBus from "@/eventBus";
import { ALERT } from "../../constants/alert.js"
import { mapGetters, mapActions } from "vuex";
import dayjs from 'dayjs';
import dayjsMixin from '@/mixins/dayjsMixin';
export default {
  mixins: [dayjsMixin],
  data() {
    return {
      tab: "accessToken",
      integrationToken: '',
      copySuccess: false,
      agentExeUrl: '',
      odAccessToken: '', // OD access token
      showToken: false,
      clinicHeaders: [
        { title: 'Clinic Num', key: 'ClinicNum'},
        { title: 'Name', key: 'Description' },
        { title: 'Abbreviation', key: 'Abbr'},
        { title: 'City', key: 'City' },
        { title: 'Phone', key: 'Phone'},
      ],
      clinics: [],
      loading: false,
      syncLogs: [],
      syncLogTotal: 0,
      syncLogOffset: 0,
      syncLogLoading: false,
      syncLogFilter: {
        action: 'ALL',
        from: dayjs().subtract(1, 'day').format('YYYY-MM-DD'),
        to: dayjs().format('YYYY-MM-DD'),
      },
      syncLogHeaders: [
        { title: 'Synced At',    key: 'synced_at',    sortable: false },
        { title: 'Patient Name', key: 'patient_name', sortable: false },
        { title: 'OD Pat Num',  key: 'od_pat_num',   sortable: false },
        { title: 'Contact ID',  key: 'contact_id',   sortable: false },
        { title: 'Action',      key: 'action',       sortable: false },
        { title: 'Event Type',  key: 'event_type',   sortable: false },
      ],
    };
  },
  async mounted() {
    if(this.isODEnabled){
        await this.fetchToken();
        await this.fetchExeFilePath();
        await this.fetchClinics();
    }
  },
  computed: {
     ...mapGetters(["isODEnabled"]),
    maskedIntegrationToken() {
      return this.maskedToken(this.integrationToken);
    }
  },
  watch: {
    tab(val) {
      if (val === 'syncLogs' && this.syncLogs.length === 0) this.fetchSyncLogs(true);
    }
  },
  methods: {
    ...mapActions(["checkIsODEnabled"]),
    async fetchExeFilePath() {
      try {
        const res = await ODConnectorService.getLatestExePath();
        this.agentExeUrl = process.env.VUE_APP_API_URL + res.data.path;
      } catch (error) {
        console.error(`Failed to fetch DSS OD Connector exe path: ${error}`);
        this.agentExeUrl = '';
      }
    },
    async fetchToken() {
      try {
        const res = await ODConnectorService.getIntegrationToken();
        this.integrationToken = res.data.token;
      } catch (error) {
        console.error(`Failed to fetch integration token: ${error}`);
        this.integrationToken = '';
      }
      try {
        const res = await ODService.getODAccessToken();
        this.odAccessToken = res.data.token;
      } catch (error) {
        console.error(`Failed to fetch OD Access token: ${error}`);
        this.odAccessToken = '';
      }
    },
    async copyToken() {
      try {
        await navigator.clipboard.writeText(this.integrationToken);
        this.copySuccess = true;
        setTimeout(() => (this.copySuccess = false), 2000);
      } catch (err) {
        console.error('Failed to copy!', err);
        this.copySuccess = false;
      }
    },
    maskedToken(token) {
      if (!token) return '';

      const length = token.length;
      const startPercent = 0.2;
      const endPercent = 0.1;
      // Calculate visible characters
      const visibleStart = Math.floor(length * startPercent);
      const visibleEnd = Math.floor(length * endPercent);

     
      if (visibleStart + visibleEnd >= length) {
        return token;
      }

      const start = token.slice(0, visibleStart);
      const end = token.slice(-visibleEnd);
      const maskedLength = length - (visibleStart + visibleEnd);

      return start + '*'.repeat(maskedLength) + end;
    },
    async handleAccessToken() {
      if(!this.odAccessToken || this.odAccessToken.trim() === ''){
        alert('Please enter a valid OD Access Token.');
        return;
      }
      if(this.isODEnabled){
        try {
          let res = await ODService.updateODAccessToken(this.odAccessToken);
          this.odAccessToken = res.data.access_token;
          EventBus.$emit("alert", { message: "Access Token Updated Succesfully", type: ALERT.SUCCESS });
          await this.fetchClinics();
        } catch (error) {
          EventBus.$emit("alert", { message: "Failed To Update OD Access Token", type: ALERT.ERROR });
          console.error(error);
        }
      }else{
        try {
          let res = await ODService.saveODAccessToken(this.odAccessToken);
          if(res.status == 201){
            this.odAccessToken = res.data.access_token;
            EventBus.$emit("alert", { message: "Access Token Saved Succesfully", type: ALERT.SUCCESS });
            await this.checkIsODEnabled();
            await this.fetchClinics();
          }
        } catch (error) {
          EventBus.$emit("alert", { message: "Failed To Save OD Access Token", type: ALERT.ERROR });
          console.error(error);
        }
      }
      
    },
    async fetchClinics(){
      try {
        this.loading = true;
        let res = await ODService.getClinics();
        this.clinics = res.data;
        this.loading = false;
      } catch (error) {
        console.error("Failed to fetch clinics:", error);
        this.loading = false;
      }
    },
    async fetchSyncLogs(reset = true) {
      if (reset) { this.syncLogOffset = 0; this.syncLogs = []; this.syncLogTotal = 0; }
      this.syncLogLoading = true;
      try {
        const params = { offset: this.syncLogOffset, limit: 50 };
        if (this.syncLogFilter.action && this.syncLogFilter.action !== 'ALL') params.action = this.syncLogFilter.action;
        if (this.syncLogFilter.from)   params.from   = this.syncLogFilter.from;
        if (this.syncLogFilter.to)     params.to     = this.syncLogFilter.to;
        const res = await ODService.getSyncLogs(params);
        this.syncLogs = reset ? res.data.data : [...this.syncLogs, ...res.data.data];
        this.syncLogTotal  = res.data.total;
        this.syncLogOffset += res.data.data.length;
      } catch (err) {
        console.error('Failed to fetch sync logs:', err);
      } finally {
        this.syncLogLoading = false;
      }
    },
    resetSyncLogFilters() {
      this.syncLogFilter = {
        action: 'ALL',
        from: dayjs().subtract(1, 'day').format('YYYY-MM-DD'),
        to: dayjs().format('YYYY-MM-DD'),
      };
      this.fetchSyncLogs(true);
    },
  }
};
</script>